import React, { useState } from 'react';
import { RotateCcw, Wand2, X, Save, Trash2, ArrowRightLeft } from 'lucide-react';
import { ColorPickerField } from '../../../components/common/colorpicker/ColorPickerModal';
import HarmonyAssignDialog from './HarmonyAssignDialog';
import ColorHarmonyModal from '../../../components/common/colorpicker/ColorHarmonyModal';
import { parseColor, toHex8 } from '../../../components/common/colorpicker/colorMath';
import {
  generateTokens, checkContrast, SEED_FIELDS, DERIVED_FIELDS, TOKEN_NAMES, TOKEN_LABELS, NEUTRAL_MODES, TOKEN_FAMILIES,
} from '../../../theme/themeGenerator';
import ThemePreview from './ThemePreview';
import StuiSelect from '../../../components/common/StuiSelect';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'colors', label: 'Colors' },
  { id: 'shape', label: 'Shape' },
  { id: 'contrast', label: 'Contrast' },
  { id: 'tokens', label: 'Tokens' },
];

const SEED_GROUPS = [
  { title: 'Brand', keys: ['primary', 'barBg'], derived: ['--color-primary-text', '--color-bar-text'] },
  { title: 'Surfaces', keys: ['surface', 'pageBg', 'text'] },
  { title: 'Status', keys: ['error', 'success', 'warning'] },
];

const FAMILIES = [
  { id: 'neutral', label: 'Grays and borders' },
  { id: 'primary', label: 'Primary shades and text on primary' },
  { id: 'status', label: 'Status backgrounds and text' },
];

const SHAPE_FIELDS = [
  { key: 'borderWidth', label: 'Line weight', options: ['1px', '2px', '3px'] },
  { key: 'focusRingWidth', label: 'Focus ring', options: ['0px', '2px', '3px'], none: '0px' },
  { key: 'radius', label: 'Corner radius', options: ['0px', '2px', '4px', '6px', '8px'] },
  { key: 'iconStroke', label: 'Icon stroke', options: ['1.5', '2', '2.5', '3'] },
];

const normalizeHex = (v) => {
  const rgba = parseColor(v);
  return rgba ? toHex8(rgba) : null;
};

const NON_COLOR = ['--border-width', '--focus-ring-width', '--radius-sm', '--icon-stroke'];
const isColorToken = (name) => !name.startsWith('--shadow-') && !NON_COLOR.includes(name);

const shortName = (name) => name.replace(/^--(color-)?/, '');
const tokenText = (name) => `${TOKEN_LABELS[name] || shortName(name)} (${shortName(name)})`;

/**
 * Theme builder popup (create and edit). `builder` is { mode, key, label, category, base,
 * status, seeds, tokens, isSystem, organizationId }; `categories` is the Theme_Category lookup values,
 * `organizations` the orgs a theme can be limited to. The key
 * is generated by the API on create and never changes, so it is display-only here. Every edit regenerates the tokens, so the live sample swatch
 * on the right always shows what Save would write. Delete and Apply to act on the saved
 * theme, so they wait until there are no unsaved changes.
 */
const ThemeBuilder = ({
  builder, setBuilder, dirty, onSave, onClose, onDelete, onApplyTo, saving, error, analyze, categories = [], organizations = [],
}) => {
  const [tab, setTab] = useState('general');
  const [harmonyOpen, setHarmonyOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [hovered, setHovered] = useState(null);          // [{ part, value, tokens }] under the pointer
  const [tokenFilter, setTokenFilter] = useState(null);  // token names picked from the swatch
  const [previewPage, setPreviewPage] = useState(analyze ? 'status' : 'grid');   // contrast results are on Status
  const { seeds, tokens } = builder;
  const overrides = seeds.overrides || [];
  const creating = builder.mode === 'create';
  const statuses = builder.isSystem ? ['Active', 'Retired'] : ['Draft', 'Active', 'Retired'];

  const patch = (p) => setBuilder(b => ({ ...b, ...p }));

  const applySeeds = (nextSeeds, base = builder.base) => {
    const s = { ...nextSeeds, base: base.toLowerCase() };
    setBuilder(b => ({ ...b, base, seeds: s, tokens: generateTokens(s, b.tokens) }));
  };

  const setSeed = (key, value) => {
    const v = normalizeHex(value);
    if (v) applySeeds({ ...seeds, [key]: v });
  };

  const setToken = (name, value) => {
    const v = normalizeHex(value);
    if (!v) return;
    setBuilder(b => ({
      ...b,
      seeds: { ...b.seeds, overrides: [...new Set([...(b.seeds.overrides || []), name])] },
      tokens: { ...b.tokens, [name]: v },
    }));
  };

  const resetToken = (name) => applySeeds({ ...seeds, overrides: overrides.filter(n => n !== name) });

  // A family is Custom while any of its tokens is set by hand (Manual); Auto resets them all.
  const familyCustom = (id) => TOKEN_FAMILIES[id].some(n => overrides.includes(n));
  const setFamily = (id, custom) => {
    const names = TOKEN_FAMILIES[id];
    const next = custom
      ? [...new Set([...overrides, ...names])]
      : overrides.filter(n => !names.includes(n));
    applySeeds({ ...seeds, overrides: next });
  };

  const openTab = (id) => { setTab(id); setTokenFilter(null); };

  // Clicking the swatch opens the Tokens tab narrowed to whatever paints the clicked item.
  const pickFromSwatch = (parts) => {
    const names = [...new Set((parts || []).flatMap(p => p.tokens))];
    if (names.length === 0) return;
    setTab('tokens');
    setTokenFilter(names);
  };

  const contrast = checkContrast(tokens);
  const failing = contrast.filter(c => !c.pass).length;
  const canLifecycle = !creating && !dirty && !saving;

  const renderGeneral = () => (
    <>
      <div className="stui-field-group">
        <label>Label <span className="stui-required">*</span></label>
        <input
          className="stui-field-input"
          value={builder.label}
          maxLength={100}
          onChange={e => patch({ label: e.target.value })}
        />
      </div>
      <div className="stui-field-group">
        <label>Category</label>
        <StuiSelect className="stui-field-input" value={builder.category || ''} onChange={e => patch({ category: e.target.value })}>
          <option value="">Uncategorized</option>
          {/* A value since deactivated stays visible on the theme that already has it */}
          {builder.category && !categories.includes(builder.category) && <option value={builder.category}>{builder.category}</option>}
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </StuiSelect>
      </div>
      <div className="stui-field-group">
        <label>Organization</label>
        <StuiSelect
          className="stui-field-input"
          value={builder.organizationId ?? ''}
          onChange={e => patch({ organizationId: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">All organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
        </StuiSelect>
        <div className="stui-sublabel">
          Limited to one organization, only its members and people working in its databases see the theme.
        </div>
      </div>
      <div className="stui-theme-builder-pair">
        <div className="stui-field-group">
          <label>Base</label>
          <StuiSelect className="stui-field-input" value={builder.base} onChange={e => applySeeds(seeds, e.target.value)}>
            <option value="Light">Light</option>
            <option value="Dark">Dark</option>
          </StuiSelect>
        </div>
        <div className="stui-field-group">
          <label>Status</label>
          <StuiSelect className="stui-field-input" value={builder.status} onChange={e => patch({ status: e.target.value })}>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </StuiSelect>
        </div>
      </div>
      {builder.status === 'Retired' && (
        <div className="stui-label-note">
          A retired theme disappears from every picker; anyone using it falls back to Standard. Their selections are
          kept, so setting it Active again restores them.
        </div>
      )}
      <div className="stui-field-group">
        <label>Key</label>
        <input className="stui-field-input" value={builder.key} readOnly tabIndex={-1} placeholder="Generated on save" />
        <div className="stui-sublabel">Assigned when the theme is first saved and never changes, so renaming leaves nothing behind.</div>
      </div>
      {builder.isSystem && <div className="stui-label-note">Built-in theme: it can be edited and retired, not deleted.</div>}
    </>
  );

  const renderSeed = (f) => (
    <div key={f.key} className="stui-theme-seed-row">
      <div>
        <span className="stui-label">{f.label}</span>
        {f.hint && <div className="stui-sublabel">{f.hint}</div>}
      </div>
      <div className="stui-theme-seed-control">
        <ColorPickerField value={seeds[f.key] || ''} title={f.label} allowAlpha={false} placeholder="No color" onChange={v => setSeed(f.key, v)} />
      </div>
    </div>
  );

  // A generated token shown beside the seeds: its generated value until set by hand.
  const renderDerived = (f) => {
    const manual = overrides.includes(f.token);
    return (
      <div key={f.token} className="stui-theme-seed-row">
        <div>
          <span className="stui-label">{f.label}</span>
          <div className="stui-sublabel">{f.hint}</div>
        </div>
        <div className="stui-theme-seed-control">
          {manual ? (
            <button type="button" className="stui-crud-btn" title="Return to the generated value" onClick={() => resetToken(f.token)}>
              <RotateCcw size={12} />
            </button>
          ) : <span className="stui-badge-gray" title="Generated from the seeds; pick a color to set it by hand">Auto</span>}
          <ColorPickerField value={tokens[f.token] || ''} title={f.label} allowAlpha={false} onChange={v => setToken(f.token, v)} />
        </div>
      </div>
    );
  };

  const renderColors = () => (
    <>
      <div className="stui-detail-section-header stui-theme-section-header">
        <span>Seed colors</span>
        <button type="button" className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setHarmonyOpen(true)}>
          <Wand2 size={12} /> Harmonize
        </button>
      </div>
      {SEED_GROUPS.map(g => (
        <React.Fragment key={g.title}>
          <div className="stui-theme-subhead">{g.title}</div>
          {g.keys.map(k => SEED_FIELDS.find(f => f.key === k)).filter(Boolean).map(renderSeed)}
          {(g.derived || []).map(t => DERIVED_FIELDS.find(f => f.token === t)).filter(Boolean).map(renderDerived)}
        </React.Fragment>
      ))}

      <div className="stui-detail-section-header"><span>Generated families</span></div>
      <div className="stui-theme-seed-row">
        <span className="stui-label">Gray tint</span>
        <div className="stui-theme-seed-control">
          <StuiSelect className="stui-field-input" value={seeds.neutralMode || 'primary'} onChange={e => applySeeds({ ...seeds, neutralMode: e.target.value })}>
            {NEUTRAL_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </StuiSelect>
        </div>
      </div>
      {(seeds.neutralMode === 'tint') && renderSeed(SEED_FIELDS.find(f => f.key === 'neutralTint'))}
      {FAMILIES.map(f => (
        <div key={f.id} className="stui-theme-seed-row">
          <span className="stui-label">{f.label}</span>
          <div className="stui-theme-seed-control">
            <StuiSelect className="stui-field-input" value={familyCustom(f.id) ? 'custom' : 'auto'} onChange={e => setFamily(f.id, e.target.value === 'custom')}>
              <option value="auto">Auto (generated)</option>
              <option value="custom">Custom (set by hand on Tokens)</option>
            </StuiSelect>
          </div>
        </div>
      ))}
    </>
  );

  const renderShape = () => (
    <>
      {SHAPE_FIELDS.map(f => (
        <div key={f.key} className="stui-theme-seed-row">
          <span className="stui-label">{f.label}</span>
          <div className="stui-theme-seed-control">
            <StuiSelect className="stui-field-input" value={String(seeds[f.key] ?? '')} onChange={e => applySeeds({ ...seeds, [f.key]: e.target.value })}>
              {!f.options.includes(String(seeds[f.key])) && seeds[f.key] != null && <option value={String(seeds[f.key])}>{String(seeds[f.key])}</option>}
              {f.options.map(o => <option key={o} value={o}>{f.none === o ? 'None (border color only)' : o}</option>)}
            </StuiSelect>
          </div>
        </div>
      ))}
      <div className="stui-label-note">Shadows follow the base (light or dark) unless set by hand on the Tokens tab.</div>
    </>
  );

  const renderContrast = () => (
    <>
      <div className="stui-label-note">WCAG AA is 4.5:1 for text. Warning only: Save is never blocked.</div>
      {contrast.map(c => (
        <div key={c.label} className="stui-theme-seed-row" title={`${c.fg} on ${c.bg}`}>
          <span className="stui-sublabel">{c.label}</span>
          <span className={c.pass ? 'stui-badge-green' : 'stui-badge-orange'}>{c.ratio.toFixed(1)}</span>
        </div>
      ))}
    </>
  );

  const renderTokens = () => (
    <>
      {tokenFilter ? (
        <div className="stui-theme-seed-row">
          <span className="stui-label-note">Tokens under the item you clicked in the sample swatch.</span>
          <button type="button" className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setTokenFilter(null)}>Show all</button>
        </div>
      ) : (
        <div className="stui-label-note">
          Every token can be edited. An edited color is marked Manual and the generator leaves it alone; its reset button
          returns it to the generated value. Click anything in the sample swatch to find its tokens.
        </div>
      )}
      {TOKEN_NAMES.filter(name => !tokenFilter || tokenFilter.includes(name)).map(name => {
        const manual = overrides.includes(name);
        const value = tokens[name] || '';
        return (
          <div key={name} className="stui-theme-token-row">
            <span className="stui-theme-token-name" title={name}>
              {TOKEN_LABELS[name] || shortName(name)} <span className="stui-sublabel">{shortName(name)}</span>
            </span>
            {manual && <span className="stui-badge-secondary" title="Set by hand: regenerating the theme keeps this value">Manual</span>}
            {isColorToken(name) ? (
              <>
                <ColorPickerField value={value} title={name} onChange={v => setToken(name, v)} />
                {manual ? (
                  <button type="button" className="stui-crud-btn" title="Return to the generated value" onClick={() => resetToken(name)}>
                    <RotateCcw size={12} />
                  </button>
                ) : <span className="stui-theme-token-spacer" />}
              </>
            ) : (
              <span className="stui-sublabel stui-theme-token-value" title={value}>{value}</span>
            )}
          </div>
        );
      })}
    </>
  );

  const body = { general: renderGeneral, colors: renderColors, shape: renderShape, contrast: renderContrast, tokens: renderTokens }[tab];

  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-xl stui-theme-builder-modal">
        <div className="stui-modal-header">
          <h3>{creating ? 'New Theme' : `Edit Theme: ${builder.label}`}{dirty && <span className="stui-theme-dirty-dot" title="Unsaved changes" />}</h3>
          <button type="button" className="stui-modal-close" onClick={onClose} disabled={saving}><X size={20} /></button>
        </div>
        <div className="stui-related-tabs stui-theme-builder-tabs">
          {TABS.map(t => (
            <button key={t.id} type="button" className={`stui-related-tab${tab === t.id ? ' active' : ''}`} onClick={() => openTab(t.id)}>
              {t.label}
              {t.id === 'contrast' && failing > 0 && <span className="stui-related-tab-count">{failing}</span>}
            </button>
          ))}
        </div>
        <div className="stui-modal-body stui-theme-builder-split">
          <div className="stui-theme-builder-pane">
            {error && <div className="stui-label-note stui-text-error">{error}</div>}
            {body()}
          </div>
          <div className="stui-theme-builder-sample">
            <div className="stui-theme-subhead">Sample swatch</div>
            <div className="stui-theme-inspect">
              {hovered && hovered.length > 0 ? hovered.map(h => (
                <div key={h.part} className="stui-sublabel">
                  <strong>{h.part}:</strong> {!h.tokens.length ? `${h.value} (not a theme token)`
                    : h.mixed ? `mix of ${h.tokens.map(tokenText).join(' + ')}` : h.tokens.map(tokenText).join(' / ')}
                </div>
              )) : <div className="stui-sublabel">Point at anything to see which tokens paint it; click to jump to them.</div>}
            </div>
            <ThemePreview tokens={tokens} analyze={analyze} onInspect={setHovered} onPick={pickFromSwatch} page={previewPage} onPage={setPreviewPage} />
          </div>
        </div>
        <div className="stui-modal-footer stui-modal-footer-split">
          <div className="stui-flex-4">
            {!creating && (
              <>
                <button
                  type="button"
                  className="stui-btn stui-btn-danger"
                  onClick={onDelete}
                  disabled={!canLifecycle || builder.isSystem}
                  title={builder.isSystem ? 'Built-in themes cannot be deleted; retire it instead' : dirty ? 'Save or discard changes first' : 'Delete this theme'}
                >
                  <Trash2 size={13} /> Delete
                </button>
                <button
                  type="button"
                  className="stui-btn stui-btn-secondary"
                  onClick={onApplyTo}
                  disabled={!canLifecycle || builder.isSystem}
                  title={builder.isSystem ? 'A built-in theme cannot be the source; edit the target directly' : dirty ? 'Save or discard changes first' : 'Copy this theme onto another, then delete this one'}
                >
                  <ArrowRightLeft size={13} /> Apply to...
                </button>
              </>
            )}
          </div>
          <div className="stui-flex-4">
            <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              type="button"
              className="stui-btn stui-btn-primary"
              onClick={onSave}
              disabled={saving || !dirty || !builder.label.trim()}
            >
              <Save size={13} /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <ColorHarmonyModal
        isOpen={harmonyOpen}
        seed={seeds.primary}
        colors={builder.harmonyColors || null}
        count={builder.harmonyColors ? builder.harmonyColors.length : undefined}
        onClose={() => setHarmonyOpen(false)}
        onApply={(hexes) => { patch({ harmonyColors: hexes }); setAssignOpen(true); }}
      />
      {assignOpen && builder.harmonyColors && (
        <HarmonyAssignDialog
          colors={builder.harmonyColors}
          current={seeds}
          onBack={() => { setAssignOpen(false); setHarmonyOpen(true); }}
          onCancel={() => setAssignOpen(false)}
          onApply={(picked) => {
            const next = { ...seeds };
            Object.entries(picked).forEach(([k, v]) => { next[k] = normalizeHex(v) || v; });
            if (picked.neutralTint) next.neutralMode = 'tint';
            applySeeds(next);
            setAssignOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ThemeBuilder;
